import * as React from 'react';
import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { Input } from '@/modules/ui/components/input/Input';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Forms';

interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.ComponentProps<'input'>, 'name'> {
  name: TName;
  label: string;
  labelSuffix?: React.ReactNode;
  description?: string;
  /** Rendered inside the field at its left edge, e.g. an icon; pad the input with `className`. */
  startAdornment?: React.ReactNode;
  /** Rendered inside the field at its right edge, e.g. a show/hide toggle. */
  endAdornment?: React.ReactNode;
}

export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  labelSuffix,
  placeholder,
  description,
  type = 'text',
  disabled,
  required,
  autoFocus,
  className,
  startAdornment,
  endAdornment,
}: FormInputProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between">
            <FormLabel required={required}>{label}</FormLabel>
            {labelSuffix}
          </div>
          <div className="relative">
            {startAdornment}
            <FormControl>
              <Input
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                className={className}
                {...field}
              />
            </FormControl>
            {endAdornment}
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
